import React, { useEffect, useRef, useState } from "react";
import VesselPositionImg from "../Media/vessel_position.svg";
import UserVesselPositionImg from "../Media/user_vessel_position.svg";
import { Button } from "reactstrap";
import { MapContainer, TileLayer, Marker } from "react-leaflet";
import L from "leaflet";

const VesselPositionMapStructure = ({
  setPopup,
  isPopup,
  imgSize,
  VesselDetailForMap,
}: any) => {
  const [hoveredVessel, setHoveredVessel] = useState<any>(null);
  const [cursorPos, setCursorPos] = useState({ x: 0, y: 0 });

  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);

  /** Prevent scroll when small map (non-popup) */
  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    const onWheel = (e: WheelEvent) => {
      if (!isPopup && wrapper.contains(e.target as Node)) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    window.addEventListener("wheel", onWheel, { passive: false, capture: true });
    return () => {
      window.removeEventListener("wheel", onWheel, { capture: true } as any);
    };
  }, [isPopup]);

  /** Fix tile layout when popup opens or screen resizes */
  useEffect(() => {
    const invalidate = () => {
      if (mapRef.current) {
        mapRef.current.invalidateSize();
        setTimeout(() => mapRef.current?.invalidateSize(), 200);
      }
    };

    invalidate();
    const timeout = setTimeout(() => isPopup && invalidate(), 50);

    const onResize = () => invalidate();
    window.addEventListener("resize", onResize);

    return () => {
      clearTimeout(timeout);
      window.removeEventListener("resize", onResize);
    };
  }, [isPopup]);

  /** Open popup */
  const handleWrapperClick = (e: React.MouseEvent) => {
    if (!isPopup) {
      e.stopPropagation();
      setPopup(true);
    }
  };

  return (
    <React.Fragment>
      {/* Zoom buttons in popup */}
      {isPopup && (
        <div
          className="ele_row1 gap-4"
          style={{
            zIndex: 1200,
            position: "absolute",
            right: 12,
            top: 12,
          }}
        >
          <Button color="primary" onClick={() => mapRef.current?.zoomIn()}>
            <i className="bx bx-zoom-in font-16" />
          </Button>

          <Button color="primary" onClick={() => mapRef.current?.zoomOut()}>
            <i className="bx bx-zoom-out font-16" />
          </Button>
        </div>
      )}

      {/* MAP WRAPPER */}
      <div
        ref={wrapperRef}
        id="map_wrapper"
        onClick={handleWrapperClick}
        style={{
          width: "100%",
          height: !isPopup ? "17rem" : "75vh",
          position: "relative",
          touchAction: "none",
          cursor: !isPopup ? "pointer" : "grab",
        }}
      >
        <MapContainer
          ref={(mapInstance) => {
            mapRef.current = mapInstance as any;
          }}
          center={[20, 0]}
          zoom={isPopup ? 2 : 1}
          minZoom={isPopup ? 2 : 1}
          maxZoom={isPopup ? 18 : 1}
          zoomControl={false}
          worldCopyJump={true}
          style={{ height: "100%", width: "100%" }}
          maxBounds={[
            [-90, -180],
            [90, 180],
          ]}
          dragging={!!isPopup}
          scrollWheelZoom={!!isPopup}
          doubleClickZoom={!!isPopup}
          touchZoom={!!isPopup}
          attributionControl={false}
        >
          <TileLayer
            url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
            subdomains={["a", "b", "c", "d"]}
            noWrap={true}
          />

          {Array.isArray(VesselDetailForMap) &&
            VesselDetailForMap.map((v: any, index: number) => {
              const icon = L.icon({
                iconUrl: v.user_assigned
                  ? UserVesselPositionImg
                  : VesselPositionImg,
                iconSize: [imgSize, imgSize],
                iconAnchor: [imgSize / 2, imgSize / 2],
              });

              return (
                <Marker
                  key={index}
                  position={[v.latitude, v.longitude]}
                  icon={icon}
                  eventHandlers={{
                    mouseover: () => {
                      setHoveredVessel(v);
                    },
                    mousemove: (e: any) => {
                      setCursorPos({
                        x: e.originalEvent.clientX,
                        y: e.originalEvent.clientY,
                      });
                    },
                    mouseout: () => {
                      setHoveredVessel(null);
                    },
                  }}
                />
              );
            })}
        </MapContainer>
      </div>

      {/* FLOATING TOOLTIP */}
      {hoveredVessel && (
        <div
          style={{
            position: "fixed",
            top: cursorPos.y + 15,
            left: cursorPos.x + 15,
            background: "rgba(31, 45, 65, 0.95)",
            color: "white",
            padding: "12px 14px",
            borderRadius: "6px",
            zIndex: 5000,
            pointerEvents: "none",
            width: "250px",
            boxShadow: "0 4px 15px rgba(0,0,0,0.25)",
            fontSize: "13px",
            lineHeight: "1.4",
            transition: "opacity 0.1s ease-out",
          }}
        >
          <div><strong>Vessel :</strong> {hoveredVessel.vessel_name}</div>
          <div><strong>Report Date-Time :</strong> {hoveredVessel.local_time ?? "-"}</div>
          <div><strong>Arrival Port :</strong> {hoveredVessel.arrival_port_name ?? "-"}</div>
          <div><strong>Departure Port :</strong> {hoveredVessel.departure_port_name ?? "-"}</div>
          <div><strong>Latitude :</strong> {hoveredVessel.latitude}</div>
          <div><strong>Longitude :</strong> {hoveredVessel.longitude}</div>
        </div>
      )}
    </React.Fragment>
  );
};

export default VesselPositionMapStructure;
